import Vue from 'vue';
import {HTTP} from "@/utils/httpCommonUtil";
import {UserRoleEnum} from "@/enums/userRolesEnum";
import MergeableInvoiceItem from "@/components/invoice/merge/MergeableInvoiceItem";
import VueSimpleSpinner from "vue-simple-spinner";
import router from "@/router";
import {formatToTimeZone} from "date-fns-timezone";
import {getRestResponseErrorMessage} from "@/utils/serverErrorMessageUtil";

export default Vue.extend({
  name: 'PreInvoiceMerge',
  props: {
    facilityId: {type: String},
    specialtyId: {type: String},
    invoiceDate: {type: String},
    mergedInvoiceId: {type: [String, Number]},
  },
  components: {
    MergeableInvoiceItem,
    VueSimpleSpinner,
  },
  filters: {
    weekFormat (dateISO: string) {
      return formatToTimeZone(dateISO, 'MMMM D', {timeZone: 'UTC'});
    }
  },
  computed: {
    canMerge() : boolean {
      return this.selectionsHaveChanged && this.mergeableInvoices.filter(invoice => invoice.selected).length > 1;
    },
    selectionsHaveChanged() : boolean {
      return this.mergeableInvoices.some((invoice: any) => {
        return invoice.selected !== this.initialSelectedMap.get(invoice.invoiceId);
      })
    },
    filterInvoices(): any[] {
      if (this.selectedFacilityId) {
        return this.mergeableInvoices.filter(invoice => invoice.facilityId === this.selectedFacilityId)
      }
      return this.mergeableInvoices
    },
  },
  data () {
    return {
      selectedFacilityId: 0,
      selectedFacilityName: '',
      initialSelectedMap: new Map<number, boolean>(),
      isAuthorized: false,
      showActionsDropdown: false,
      loadingInitialData: false,
      mergeableInvoices: [] as any[],
      startDate: null,
      endDate: null,
    }
  },
  beforeMount: function () {
    this.isAuthorized = this.$store.getters['user/hasRole']([
      UserRoleEnum.MANAGER,
      UserRoleEnum.ACCOUNTANT
    ]);
  },
  async created () {
    if (this.facilityId) {
      this.selectedFacilityId = Number(this.facilityId);
    }
    await this.loadData();
  },
  methods: {
    invoiceSelectionChanged(invoice: any) {
      const allSelected = this.mergeableInvoices.filter(invoice => invoice.selected);
      if (invoice.selected) {
        if (allSelected.length === 1) {
          this.selectedFacilityId = invoice.facilityId;
          this.selectedFacilityName = invoice.facilityName;
        }
      } else {
        if (!allSelected.length) {
          this.selectedFacilityId = 0;
          this.selectedFacilityName = '';
        }
      }

    },
    facilitySelected (facility: any) {
      this.selectedFacilityId = facility.id;
      this.selectedFacilityName = facility.name;
    },
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    async mergeInvoices() {
      this.loadingInitialData = true;
      try {
        const invoicesToMerge = this.mergeableInvoices.filter(invoice => invoice.selected).map(invoice => {
          return {
            facilityId: invoice.facilityId,
            specialtyId: invoice.invoiceWeeks[0].specialtyId,
            invoiceStartDate: invoice.invoiceStartDate
          };
        });
        const response = this.mergedInvoiceId
            ? await HTTP.put(`/api/invoices/merge/${this.mergedInvoiceId}`, {preInvoices: invoicesToMerge})
            : await HTTP.post(`/api/invoices/merge`, {preInvoices: invoicesToMerge});
        this.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error Merging Invoices.");
        }
        const mergedIdToNav = this.mergedInvoiceId ? this.mergedInvoiceId : response.data.data.mergedInvoiceId;
        router.push({name: 'MergedInvoice', params: {mergedInvoiceId:mergedIdToNav}});
      } catch (err) {
        let generalError = getRestResponseErrorMessage(err);
        this.loadingInitialData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: generalError ? generalError : 'An error occurred merging selected pre invoices.'
        });
      }
    },
    async revertMerge() {
      this.loadingInitialData = true;
      try {
        const response = await HTTP.delete(`/api/invoices/merge/${this.mergedInvoiceId}`);
        this.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error Reverting Merged Invoice.");
        }
        router.push({name: 'Invoices'});
      } catch (err) {
        this.loadingInitialData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred deleting Merged Invoices.'
        });
      }
    },
    cancelChanges() {
      this.mergeableInvoices.forEach(invoice => {
        invoice.selected = this.initialSelectedMap.get(invoice.invoiceId);
      });
      this.showActionsDropdown = false;
    },
    async loadData () {
      this.loadingInitialData = true;
      try {
        let parameters = '';
        if (this.mergedInvoiceId) {
          parameters = `mergedInvoiceId=${this.mergedInvoiceId}`;
        } else if (this.facilityId) {
          parameters = `facilityId=${this.facilityId}`;
        }
        const response = await HTTP.get(`/api/invoices/mergeable?${parameters}`);
        this.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Mergeable Invoices.");
        }
        this.startDate = response.data.data.startDate;
        this.endDate = response.data.data.endDate;
        const invoices = response.data.data.mergeableInvoices;
        invoices.forEach((invoice: any) => {
          invoice.selected = !!invoice.mergedInvoiceId;
        });
        const primaryInvoice = invoices.filter((invoice: any) => {
          return invoice.facilityId === Number(this.facilityId) &&
            invoice.invoiceWeeks.some((invoiceWeek:any) => {
              const invoiceStartDate = invoiceWeek.invoiceStartDate.substr(0,10);
              return invoiceStartDate === this.invoiceDate
                && invoiceWeek.specialtyId === Number(this.specialtyId)
            });
        });
        if (primaryInvoice[0]) {
          primaryInvoice[0].selected = true;
        }
        invoices.sort((a:any,b:any) => {
          return (a.selected === b.selected) ? 0 : a.selected ? -1 : 1;
        });
        const initialSelectedMap = new Map<number, boolean>();
        invoices.forEach((invoice: any) => {
          initialSelectedMap.set(invoice.invoiceId, invoice.selected);
        });
        this.initialSelectedMap = initialSelectedMap;
        this.mergeableInvoices = invoices;
      } catch (err) {
        this.loadingInitialData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving Invoice Information.'
        });
      }
    }
  }
});



// WEBPACK FOOTER //
// ./src/components/invoice/merge/PreInvoiceMerge.ts